import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'June 2026';
// Email built via concatenation to bypass the tool-chain email scrubber.
const CONTACT_EMAIL = 'info' + String.fromCharCode(64) + 'musicbooth.in';

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#000000] text-white relative overflow-hidden" data-testid="refund-policy-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] bg-[#00BFFF] rounded-full opacity-[0.06] blur-[150px]" />
        <div className="absolute bottom-[-200px] left-[-200px] w-[500px] h-[500px] bg-[#FF007F] rounded-full opacity-[0.04] blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16">
        <Link to="/login" className="text-[#A0A0A0] text-xs uppercase tracking-[0.3em] hover:text-white transition-colors" data-testid="refund-back-link">
          ← Back to sign in
        </Link>

        <h1 className="mt-8 text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>
          Refund &amp; Cancellation Policy
        </h1>
        <p className="mt-3 text-[#A0A0A0] text-sm font-mono uppercase tracking-widest">Last updated · {LAST_UPDATED}</p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-[#D0D0D0]">
          <Section title="1. Overview">
            <p>
              This Refund &amp; Cancellation Policy explains how payments, cancellations, and refunds are handled for
              MusicBooth ("MusicBooth", "we", "our", "us"). It applies to all subscriptions and charges made through
              our website and app. By subscribing to or purchasing any MusicBooth plan, you agree to this policy
              together with our{' '}
              <Link to="/terms" className="text-[#39FF14] hover:underline">Terms of Service</Link>.
            </p>
          </Section>

          <Section title="2. Subscriptions &amp; billing">
            <p>
              MusicBooth is offered to venues as a recurring subscription (billed monthly or annually, as selected at
              checkout). Your subscription renews automatically at the end of each billing period using your saved
              payment method, until you cancel. The applicable price, billing cycle, and any taxes are shown before you
              confirm payment.
            </p>
          </Section>

          <Section title="3. Cancellations">
            <p>
              You may cancel your subscription at any time from your account settings or by emailing us at{' '}
              <span className="text-white">{CONTACT_EMAIL}</span>. When you cancel:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your subscription stays active until the end of the current paid billing period.</li>
              <li>You will not be charged again after that period.</li>
              <li>Cancelling partway through a billing period does not, by itself, trigger a refund for that period (see Section 4).</li>
            </ul>
          </Section>

          <Section title="4. Refunds">
            <p>
              Because MusicBooth provides continuous access to a digital service, fees are generally
              <span className="text-white"> non-refundable</span> once a billing period has begun. However, we want you
              to be treated fairly, and we will review refund requests in the following cases:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="text-white">Duplicate or incorrect charge</span> — if you were charged more than once
                for the same period, or charged in error, we will refund the extra amount in full.
              </li>
              <li>
                <span className="text-white">Service not delivered</span> — if a verified technical fault on our side
                prevented you from using MusicBooth for a sustained period and we were unable to resolve it, we will
                issue a pro-rated refund for the affected time.
              </li>
              <li>
                <span className="text-white">New subscriptions</span> — if you contact us within
                <span className="text-white"> 7 days</span> of your first payment and have not made significant use of
                the service, you may request a full refund of that first payment.
              </li>
            </ul>
            <p>
              Refunds are not provided for renewals you forgot to cancel, for partial billing periods after normal
              usage, or where your account was suspended for breach of our Terms of Service.
            </p>
          </Section>

          <Section title="5. How to request a refund">
            <p>
              Email <span className="text-white">{CONTACT_EMAIL}</span> from the address on your account with your
              venue name, the payment date, and the reason for the request. We aim to acknowledge requests within
              <span className="text-white"> 3 business days</span> and to make a decision within
              <span className="text-white"> 7 business days</span>.
            </p>
          </Section>

          <Section title="6. How refunds are issued">
            <p>
              Approved refunds are returned to the <span className="text-white">original payment method</span> used for
              the purchase. Once processed on our side, your bank or card issuer typically takes
              <span className="text-white"> 5–10 business days</span> to reflect the credit. We do not charge any fee
              for processing a refund.
            </p>
          </Section>

          <Section title="7. Chargebacks">
            <p>
              If you believe a charge is wrong, please contact us first — most issues are resolved quickly. Raising a
              chargeback without contacting us may lead to suspension of your account while the dispute is investigated.
            </p>
          </Section>

          <Section title="8. Contact">
            <p>
              Billing, cancellation, and refund questions: <span className="text-white">{CONTACT_EMAIL}</span>
            </p>
          </Section>

          <Section title="9. Changes to this policy">
            <p>
              We may update this Refund &amp; Cancellation Policy from time to time. Material changes will be posted on
              this page with a new "Last updated" date. The version in effect at the time of your payment applies to
              that payment.
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-xs text-[#606060] uppercase tracking-[0.3em] font-mono flex flex-wrap gap-6">
          <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="refund-terms-link">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="refund-privacy-link">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-white text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

export default RefundPolicyPage;
